import { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { buildPortfolioFromEntity } from '@/admins/SmartReports/helpers';
import { usePagination } from '@/admins/SmartReports/hooks/usePagination';
import { consistentHash } from '@/utils/consistentHash';
import { ACTION_TYPES, editSmartReportReducer, INITIAL_STATE } from './EditSmartReportReducer';

const parseEntityId = entityId => {
  if (typeof entityId === 'number') return entityId;
  if (typeof entityId === 'string' && !Number.isNaN(entityId)) {
    return parseInt(entityId, 10);
  }
  return entityId;
};

const EditSmartReportContext = createContext(null);

const EditSmartReportProvider = ({ children }) => {
  const [state, dispatch] = useReducer(editSmartReportReducer, INITIAL_STATE);

  const entity = useMemo(() => {
    const entityParent = state.report?.smartReportEntities?.[0];
    if (!entityParent?.entity) return null;

    return {
      id: parseEntityId(entityParent.entityId),
      name: entityParent.entity.name,
      type: entityParent.entityType,
    };
  }, [state.report]);

  const portfolio = useMemo(() => buildPortfolioFromEntity(entity), [entity]);

  const pages = useMemo(() => {
    return (state.reportVersion?.pages || []).map(({ key, ...page }) => ({
      ...page,
      key: key || consistentHash(page),
    }));
  }, [state.reportVersion]);

  const setReport = useCallback(report => {
    dispatch({
      type: ACTION_TYPES.SET_REPORT,
      payload: { report },
    });
  }, []);

  const setSelectedPage = useCallback(page => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_PAGE,
      payload: { page },
    });
  }, []);

  const setSelectedLayout = useCallback(layout => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_LAYOUT,
      payload: { layout },
    });
  }, []);

  const { nextPage, previousPage } = usePagination({
    selectedPage: state.selectedPage,
    pages,
    setSelectedPage,
  });

  const addPage = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.ADD_PAGE,
      payload: { layout: state.selectedLayout },
    });
  }, [state.selectedLayout]);

  const removePage = useCallback(page => {
    if (!page?.number) return;

    dispatch({
      type: ACTION_TYPES.REMOVE_PAGE,
      payload: { pageNumber: page.number },
    });
  }, []);

  const resetPageSlot = useCallback(slotId => {
    if (!slotId) return;

    dispatch({
      type: ACTION_TYPES.REMOVE_PAGE_ELEMENT,
      payload: { slotId },
    });
  }, []);

  const applyLayout = useCallback(layout => {
    if (!layout?.id) return;

    dispatch({
      type: ACTION_TYPES.APPLY_LAYOUT,
      payload: { layout },
    });
  }, []);

  const onComponentLoad = useCallback(({ componentId, slotId, data }) => {
    if (!componentId || !slotId) return;

    dispatch({
      type: ACTION_TYPES.UPDATE_PAGE_ELEMENT,
      payload: { componentId, slotId, data },
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      report: state.report,
      reportVersion: state.reportVersion,
      selectedPage: state.selectedPage,
      selectedLayout: state.selectedLayout,

      // derived state
      entity,
      portfolio,
      pages,

      setReport,
      setSelectedPage,
      setSelectedLayout,
      addPage,
      removePage,
      nextPage,
      previousPage,
      onComponentLoad,
      resetPageSlot,
      applyLayout,
    }),
    [
      state,
      entity,
      portfolio,
      pages,
      setReport,
      setSelectedPage,
      setSelectedLayout,
      addPage,
      removePage,
      nextPage,
      previousPage,
      onComponentLoad,
      resetPageSlot,
      applyLayout,
    ],
  );

  return (
    <EditSmartReportContext.Provider value={contextValue}>
      {children}
    </EditSmartReportContext.Provider>
  );
};

const useEditSmartReport = () => useContext(EditSmartReportContext);

export { EditSmartReportProvider, useEditSmartReport };
